import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import BrandMark from '../BrandMark';

interface AuthShellProps {
  title: string;
  description: string;
  children: ReactNode;
  footer?: ReactNode;
  headerSlot?: ReactNode;
  eyebrow?: string;
}

const marketingBaseUrl = import.meta.env.VITE_MARKETING_BASE_URL || '/';

export default function AuthShell({
  title,
  description,
  children,
  footer,
  headerSlot,
  eyebrow,
}: AuthShellProps) {
  return (
    <div className="public-theme min-h-screen bg-background text-text">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,135,90,0.08),transparent_52%)]"
      />

      <div className="relative mx-auto flex min-h-screen max-w-lg flex-col px-4 py-6 sm:px-6">
        <header className="flex items-center justify-between gap-4">
          <a href={marketingBaseUrl} className="inline-flex items-center">
            <BrandMark size="sm" titleClassName="text-lg" />
          </a>

          <a
            href={marketingBaseUrl}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-text-muted transition-colors hover:text-text"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </a>
        </header>

        <main className="flex flex-1 flex-col justify-center py-10 sm:py-14">
          {eyebrow ? (
            <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/15 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
              {eyebrow}
            </div>
          ) : null}

          <h1 className="mt-4 text-[28px] font-bold tracking-tight leading-tight text-text sm:text-[32px]">
            {title}
          </h1>
          <p className="mt-3 text-base leading-relaxed text-text-muted">
            {description}
          </p>

          {headerSlot ? <div className="mt-8">{headerSlot}</div> : null}

          <div className="mt-8">{children}</div>

          {footer ? <div className="mt-8 border-t border-border pt-6">{footer}</div> : null}
        </main>

        <footer className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-text-muted">
          <a href="/privacy" className="transition-colors hover:text-text">Privacy</a>
          <a href="/terms" className="transition-colors hover:text-text">Terms</a>
          <a href="/data-request" className="transition-colors hover:text-text">Data Request</a>
          <a href="/accessibility" className="transition-colors hover:text-text">Accessibility</a>
        </footer>
      </div>
    </div>
  );
}
